import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown, ChevronUp } from "lucide-react";

import Tooltip from "@/components/ui/tooltip";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { SIDEBAR_COLLAPSED_WIDTH, centerInCollapsedRail } from "./constants";
import { cn } from "@/utils/cn";

type SidebarNavListItemProps = {
  depth: number;
  href: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon?: React.ComponentType<any>;
  badge?: string;
  open?: boolean;
  title: string;
  collapsed?: boolean;
  children?: React.ReactNode;
};

const NESTED_PADDING = "py-2 pl-14 pr-4";
// Matches iconClasses' h-5 w-5 — passed to centerInCollapsedRail so the pl
// that keeps the icon anchored is derived from SIDEBAR_COLLAPSED_WIDTH
// instead of a hand-copied pixel value (see constants.ts).
const ROW_ICON_SIZE = 20;

function SidebarNavListItem({
  title,
  href,
  depth = 0,
  children,
  icon: Icon,
  badge,
  open: openProp = false,
  collapsed = false,
}: SidebarNavListItemProps) {
  const [open, setOpen] = useState(openProp);
  const location = useLocation();

  useEffect(() => {
    setOpen(openProp);
  }, [openProp]);

  const handleToggle = () => setOpen((state) => !state);

  const isActive =
    !children &&
    (location.pathname === href || location.pathname.startsWith(`${href}/`));

  // When clicking a nav item that points to the current route, preserve the
  // existing query params so URL-driven state (selected properties, filters, etc.)
  // isn't wiped on re-click.
  const resolvedTo = isActive ? `${href}${location.search}` : href;

  // Tailwind preflight is disabled app-wide (MUI coexistence), so NavLink's <a>
  // would otherwise fall back to the browser's default link styling — reset here.
  const baseClasses =
    "group relative flex items-center border-0 font-normal text-inherit no-underline transition-colors focus-visible:outline-none";
  // min-h-14 (expanded only — badges never render collapsed, so it'd just
  // add dead space to the icon-only rail) gives the row enough headroom for
  // a two-line title (title + badge wrapping) without growing when that
  // happens — otherwise a badge appearing/disappearing (see PricingAudit.tsx's
  // gpcPendingCount/gprtAnsweredCount) visibly pushes every row below it.
  const layoutClasses =
    depth > 0
      ? cn("justify-start", NESTED_PADDING)
      : cn("justify-start py-3", collapsed ? "pr-[22px]" : "min-h-14 pr-7");
  // pl is computed (not a pl-[Npx] class) and applied the same collapsed or
  // not, via inline style — same reason width/minWidth below are inline: a
  // JS-derived value in a class string breaks Tailwind's content-scanning.
  // Keeping it fixed across both states is what keeps the icon's horizontal
  // anchor from shifting when the sidebar toggles.
  const rowStyle =
    depth > 0
      ? undefined
      : {
          paddingLeft: centerInCollapsedRail(ROW_ICON_SIZE),
          ...(collapsed
            ? {
                width: SIDEBAR_COLLAPSED_WIDTH,
                minWidth: SIDEBAR_COLLAPSED_WIDTH,
              }
            : {}),
        };

  // Opacity now depends only on active state, never on collapsed — previously
  // icons were opacity-100 collapsed but opacity-50 expanded, so the sidebar
  // visibly dimmed on open even though nothing else changed.
  const iconClasses = cn(
    "h-5 w-5 shrink-0 text-sidebar-color transition-opacity",
    isActive ? "opacity-100" : "opacity-90"
  );
  const labelClasses = cn(
    "flex min-w-0 flex-1 items-center px-4 text-[0.8125rem]",
    isActive ? "font-semibold text-sidebar-color" : "text-sidebar-color/90"
  );

  const itemContent = (
    <>
      {Icon && <Icon className={iconClasses} />}
      {!collapsed && (
        <span className={labelClasses}>
          <span className="whitespace-normal break-words leading-tight">
            {title}
          </span>
          {badge && (
            // ml-auto (not ml-2) pins the badge to the row's right edge
            // regardless of title length, so it lines up in the same spot
            // across rows instead of trailing right after whichever title
            // happens to be shorter/longer.
            <span className="ml-auto inline-flex h-5 shrink-0 items-center rounded-full bg-sidebar-badge-background px-2 text-[11px] font-bold text-sidebar-badge-color">
              {badge}
            </span>
          )}
        </span>
      )}
      {children &&
        !collapsed &&
        (open ? (
          <ChevronUp className="h-4 w-4 shrink-0 text-sidebar-color/70" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-sidebar-color/70" />
        ))}
    </>
  );

  // Depth-0 rows get their vertical gap from the flex `gap` wrapper in
  // SidebarNavSection.tsx instead — adjacent block-level margins collapse to
  // the larger one rather than summing, which made the old my-0.5 read as
  // half its intended size. Nested rows aren't in that wrapper, so they still
  // need their own margin.
  const spacingClasses = depth > 0 ? "my-0.5" : "";
  const sharedRowClasses = cn(
    baseClasses,
    layoutClasses,
    spacingClasses,
    "rounded-md"
  );

  const content = children ? (
    <button
      type="button"
      onClick={handleToggle}
      aria-expanded={open}
      style={rowStyle}
      className={cn(
        sharedRowClasses,
        "w-full bg-transparent hover:bg-white/[0.08]"
      )}
    >
      {itemContent}
    </button>
  ) : (
    <NavLink
      to={resolvedTo}
      style={rowStyle}
      className={cn(
        sharedRowClasses,
        isActive
          ? "bg-white/[0.16] hover:bg-white/[0.16]"
          : "bg-transparent hover:bg-white/[0.08]"
      )}
    >
      {itemContent}
    </NavLink>
  );

  const finalContent = collapsed ? (
    <Tooltip content={title} side="right">
      {content}
    </Tooltip>
  ) : (
    content
  );

  // A real wrapper (not a Fragment) so this item is exactly one flex child of
  // SidebarNavSection.tsx's gap-2 container — Radix's Collapsible.Root always
  // renders a div even when closed, so without this wrapper that div would
  // become its own flex sibling and pick up a phantom gap independent of
  // whether the submenu is actually open.
  return (
    <div>
      {finalContent}
      {children && !collapsed && (
        <Collapsible open={open}>
          <CollapsibleContent>{children}</CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}

export default SidebarNavListItem;
